/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { DefaultButton, IButtonProps, IButtonStyles, PrimaryButton } from '@fluentui/react/lib/Button';
import { IDialogContentStyleProps, IDialogContentStyles } from '@fluentui/react/lib/Dialog';
import { FocusZone, FocusZoneDirection, IFocusZoneProps } from '@fluentui/react/lib/FocusZone';
import { IListProps, List } from '@fluentui/react/lib/List';
import { Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylistV2 } from 'src/core/themes/luisStylist';

export type EntityMigrationPermissionModalProps = BasicModalProps<object> & {
    /**
     * Array of the names of the composite entities
     * need to migrate.
     */
    compositeNames: string[];
};

const messages = defineMessages({
    title: {
        id: 'luis.entityMigrationPermissionModal.title',
        defaultMessage: 'Upgrade your composite entities'
    },
    upgradeSubtitle: {
        id: 'luis.entityMigrationPermissionModal.upgradeSubtitle',
        defaultMessage: 'Upgrade your composite entities for:'
    },
    upgradeBenefitOne: {
        id: 'luis.entityMigrationPermissionModal.upgradeBenefitOne',
        defaultMessage: 'Improved accuracy'
    },
    upgradeBenefitTwo: {
        id: 'luis.entityMigrationPermissionModal.upgradeBenefitTwo',
        defaultMessage: 'Better debuggability'
    },
    learnMore: {
        id: 'luis.entityMigrationPermissionModal.learnMore',
        defaultMessage: 'Learn more about the new and improved luis.'
    },
    detectedCompositesTitle: {
        id: 'luis.entityMigrationPermissionModal.detectedCompositesTitle',
        defaultMessage: 'We have detected {compositesCount} composite entities in your app:'
    },
    upgradeButtonText: {
        id: 'luis.entityMigrationPermissionModal.upgradeButtonText',
        defaultMessage: 'Upgrade now'
    },
    remindButtonText: {
        id: 'luis.entityMigrationPermissionModal.remindButtonText',
        defaultMessage: 'Remind me later'
    }
});

const { styleComponent, styleUlist, styleLi } = getStylistV2('EntityMigrationPermissionModal');
const ShiftedUList = styleUlist('ShiftedUList', { margin: 0, listStyle: 'unset', paddingInlineStart: '30px' });
const TailedListItem = styleLi('TailedListItem', { margin: '8px 0', fontWeight: 600 });
const FocusZoneListWrapper = styleComponent<IFocusZoneProps>(FocusZone)('FocusZoneListWrapper', {
    overflow: 'auto',
    minHeight: '70px',
    maxHeight: '120px'
});
const ShiftedScrollableList = styleComponent<IListProps>(List)('ShiftedScrollableList', { paddingLeft: '27px' });

const dialogStyles = classNamesFunction<IDialogContentStyleProps, IDialogContentStyles>()({ title: { padding: '24px' } });
const upgradeButtonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({ root: { marginRight: '10px' } });
const listItemTextStyles = classNamesFunction<IButtonProps, IButtonStyles>()({ root: { paddingBottom: '4px' } });

/**
 * Displays the initial modal for the entity migration process
 * which includes the benefits behind the migration and the composite
 * entities exist in the application to be migrated.
 */
export const EntityMigrationPermissionModal = (props: EntityMigrationPermissionModalProps) => {
    const { compositeNames, onSubmit, onDismiss, ...rest } = props;
    const { localize } = useLocalization();

    const title = (
        <Stack horizontalAlign="center" grow tokens={{ childrenGap: 24 }}>
            <LuisText variant="xLarge" fontWeight="bold">
                {localize(messages.title)}
            </LuisText>
        </Stack>
    );

    return (
        <BasicModal
            {...rest}
            valid
            minWidth="580px"
            modalProps={{ isBlocking: false }}
            styles={{ main: { minHeight: '400px' } }}
            dialogContentProps={{ title, styles: dialogStyles }}
            onRenderFooter={() => (
                <>
                    <PrimaryButton onClick={onSubmit} styles={upgradeButtonStyles} text={localize(messages.upgradeButtonText)} />
                    <DefaultButton onClick={onDismiss}>{localize(messages.remindButtonText)}</DefaultButton>
                </>
            )}>
            <Stack tokens={{ childrenGap: 24, padding: '20px 0' }} styles={{ root: { width: '480px' } }}>
                <Stack>
                    <LuisText>{localize(messages.upgradeSubtitle)}</LuisText>

                    <ShiftedUList>
                        <TailedListItem>{localize(messages.upgradeBenefitOne)}</TailedListItem>
                        <TailedListItem>{localize(messages.upgradeBenefitTwo)}</TailedListItem>
                    </ShiftedUList>

                    <ExternalLink href="https://go.microsoft.com/fwlink/?linkid=2128339">{localize(messages.learnMore)}</ExternalLink>
                </Stack>

                <Stack tokens={{ childrenGap: 12 }}>
                    <LuisText>{localize(messages.detectedCompositesTitle, { compositesCount: compositeNames.length })}</LuisText>

                    <FocusZoneListWrapper data-is-scrollable direction={FocusZoneDirection.vertical} isCircularNavigation>
                        <ShiftedScrollableList
                            items={compositeNames}
                            onRenderCell={name => (
                                <LuisText data-is-focusable fontWeight="bold" lineHeight="20px" styles={listItemTextStyles}>
                                    {name}
                                </LuisText>
                            )}
                        />
                    </FocusZoneListWrapper>
                </Stack>
            </Stack>
        </BasicModal>
    );
};
